import { FONT_FAMILY, getFontString } from "@excalidraw/common";

import { measureText, charWidth } from "@excalidraw/element";

import type {
  ExcalidrawTextElement,
  FontFamilyValues,
} from "@excalidraw/element/types";

import type { Radians } from "@excalidraw/math";

import { FontLoadingManager } from "../fonts/FontLoadingManager";
import { FontErrorHandler } from "../fonts/FontErrorHandler";
import { brandedTypes } from "../tests/helpers/brandedTypes";

/**
 * Enhanced font system integration utilities
 * Ensures proper text metrics, font loading, and export compatibility
 */

/**
 * Update text metrics for elements with new fonts
 * Ensures text elements maintain proper dimensions when fonts change
 */
export const updateTextMetricsForNewFonts = (
  elements: readonly ExcalidrawTextElement[],
): ExcalidrawTextElement[] => {
  return elements.map((element) => {
    try {
      // Validate font family
      const fontLoadingManager = FontLoadingManager.getInstance();
      const validatedFontFamily = fontLoadingManager.validateFontFamily(
        element.fontFamily,
      );

      // If font family was corrected, update the element
      const fontFamily =
        validatedFontFamily !== element.fontFamily
          ? validatedFontFamily
          : element.fontFamily;

      // Get font string for measurements
      const fontString = getFontString({
        fontFamily,
        fontSize: element.fontSize,
      });

      // Clear any cached measurements for this font to ensure fresh calculations
      charWidth.clearCache(fontString);

      // Measure text with the current font
      const metrics = measureText(element.text, fontString, element.lineHeight);

      // Update element with new metrics if font family changed
      if (fontFamily !== element.fontFamily) {
        return {
          ...element,
          fontFamily,
          width: metrics.width,
          height: metrics.height,
        };
      }

      // Return original element if no changes needed
      return element;
    } catch (error) {
      console.warn(
        `Failed to update text metrics for element ${element.id}:`,
        error,
      );

      // Handle error gracefully by falling back to a safe font
      const errorHandler = FontErrorHandler.getInstance();
      const fallbackFont = errorHandler.handleFontLoadingError(
        element.fontFamily,
        error instanceof Error ? error : new Error(String(error)),
        "rendering",
      );

      if (fallbackFont !== element.fontFamily) {
        const fontString = getFontString({
          fontFamily: fallbackFont,
          fontSize: element.fontSize,
        });

        const metrics = measureText(
          element.text,
          fontString,
          element.lineHeight,
        );

        return {
          ...element,
          fontFamily: fallbackFont,
          width: metrics.width,
          height: metrics.height,
        };
      }

      return element;
    }
  });
};

/**
 * Ensure proper text wrapping and sizing with new fonts
 * Handles text wrapping when fonts change or when importing elements
 */
export const ensureProperTextWrappingAndSizing = (
  element: ExcalidrawTextElement,
  maxWidth?: number,
): ExcalidrawTextElement => {
  try {
    const fontString = getFontString({
      fontFamily: element.fontFamily,
      fontSize: element.fontSize,
    });

    // If maxWidth is provided, we might need to wrap text
    if (maxWidth && element.width > maxWidth) {
      // For now, we'll just ensure the metrics are correct
      // Real text wrapping would require more complex logic
      // This is a simplified implementation that constrains width
      const metrics = measureText(element.text, fontString, element.lineHeight);

      // If the text is too wide, we simulate wrapping by adjusting dimensions
      // In a real implementation, this would involve actual text wrapping logic
      const constrainedWidth = Math.min(metrics.width, maxWidth);
      const heightMultiplier =
        metrics.width > maxWidth ? Math.ceil(metrics.width / maxWidth) : 1;

      return {
        ...element,
        width: constrainedWidth,
        height: metrics.height * heightMultiplier,
      };
    }

    // Just ensure metrics are correct
    const metrics = measureText(element.text, fontString, element.lineHeight);

    return {
      ...element,
      width: metrics.width,
      height: metrics.height,
    };
  } catch (error) {
    console.warn(
      `Failed to ensure proper text wrapping for element ${element.id}:`,
      error,
    );
    return element;
  }
};

/**
 * Handle font embedding for export formats
 * Prepares font information for export to ensure compatibility
 */
export const handleFontEmbeddingForExport = (
  elements: readonly ExcalidrawTextElement[],
  exportFormat: "svg" | "png" | "json" = "json",
): {
  elements: ExcalidrawTextElement[];
  fontInfo: Array<{
    fontFamily: FontFamilyValues;
    fontName: string;
    isCustomFont: boolean;
  }>;
} => {
  const fontInfo: Array<{
    fontFamily: FontFamilyValues;
    fontName: string;
    isCustomFont: boolean;
  }> = [];

  const processedElements = elements.map((element) => {
    // Validate and get font information
    const fontLoadingManager = FontLoadingManager.getInstance();
    const validatedFontFamily = fontLoadingManager.validateFontFamily(
      element.fontFamily,
    );

    // Get font name
    const fontName = getFontFamilyName(validatedFontFamily);
    const isCustomFont = !isDefaultFont(validatedFontFamily);

    // Add to font info if not already present
    if (!fontInfo.some((info) => info.fontFamily === validatedFontFamily)) {
      fontInfo.push({
        fontFamily: validatedFontFamily,
        fontName,
        isCustomFont,
      });
    }

    // For SVG exports, we might need to ensure font availability
    if (exportFormat === "svg" && isCustomFont) {
      // For custom fonts in SVG, we might want to fall back to web-safe fonts
      // or include font face definitions
      console.info(`Custom font ${fontName} used in SVG export`);
    }

    return {
      ...element,
      fontFamily: validatedFontFamily,
    };
  });

  return {
    elements: processedElements,
    fontInfo,
  };
};

/**
 * Get font family name from font family value
 */
function getFontFamilyName(fontFamily: FontFamilyValues): string {
  const entry = Object.entries(FONT_FAMILY).find(
    ([, value]) => value === fontFamily,
  );
  return entry ? entry[0] : "Excalifont";
}

/**
 * Check if a font is a default font
 */
function isDefaultFont(fontFamily: FontFamilyValues): boolean {
  const defaultFonts = [
    FONT_FAMILY.Virgil,
    FONT_FAMILY.Helvetica,
    FONT_FAMILY.Cascadia,
    FONT_FAMILY.Excalifont,
  ];
  return defaultFonts.includes(fontFamily);
}

/**
 * Validate font compatibility across different platforms
 * Ensures fonts work consistently across different environments
 */
export const validateFontCompatibility = (
  elements: readonly ExcalidrawTextElement[],
): {
  compatibleElements: ExcalidrawTextElement[];
  warnings: Array<{
    elementId: string;
    fontFamily: FontFamilyValues;
    issue: string;
    suggestion: string;
  }>;
} => {
  const warnings: Array<{
    elementId: string;
    fontFamily: FontFamilyValues;
    issue: string;
    suggestion: string;
  }> = [];

  const compatibleElements = elements.map((element) => {
    const fontLoadingManager = FontLoadingManager.getInstance();
    const validatedFontFamily = fontLoadingManager.validateFontFamily(
      element.fontFamily,
    );

    if (validatedFontFamily !== element.fontFamily) {
      warnings.push({
        elementId: element.id,
        fontFamily: element.fontFamily,
        issue: "Font family not available",
        suggestion: `Falling back to ${getFontFamilyName(validatedFontFamily)}`,
      });

      return {
        ...element,
        fontFamily: validatedFontFamily,
      };
    }

    return element;
  });

  return {
    compatibleElements,
    warnings,
  };
};

/**
 * Enhanced text element creation with font validation
 * Creates text elements with validated fonts and proper metrics
 */
export const createTextElementWithValidatedFont = (opts: {
  text: string;
  originalText?: string;
  fontSize?: number;
  fontFamily?: FontFamilyValues;
  textAlign?: any;
  verticalAlign?: any;
  containerId?: string | null;
  lineHeight?: number;
  autoResize?: boolean;
  x: number;
  y: number;
}): ExcalidrawTextElement => {
  // Validate font family
  const fontLoadingManager = FontLoadingManager.getInstance();
  const validatedFontFamily = fontLoadingManager.validateFontFamily(
    opts.fontFamily || FONT_FAMILY.Excalifont,
  );

  // Get font string for measurements
  const fontString = getFontString({
    fontFamily: validatedFontFamily,
    fontSize: opts.fontSize || 20,
  });

  // Measure text with validated font
  const metrics = measureText(
    opts.text,
    fontString,
    brandedTypes.createLineHeight(opts.lineHeight || 1.25),
  );

  // Create text element with validated font and correct metrics
  return {
    type: "text",
    id: `text-${Date.now()}-${Math.random()}`,
    x: opts.x,
    y: opts.y,
    width: metrics.width,
    height: metrics.height,
    angle: 0 as Radians,
    strokeColor: "#000000",
    backgroundColor: "transparent",
    fillStyle: "solid",
    strokeWidth: 1,
    strokeStyle: "solid",
    roughness: 1,
    opacity: 100,
    groupIds: [],
    frameId: null,
    index: "a0" as any,
    roundness: null,
    seed: Math.floor(Math.random() * 2147483647),
    versionNonce: Math.floor(Math.random() * 2147483647),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
    text: opts.text,
    fontSize: opts.fontSize || 20,
    fontFamily: validatedFontFamily,
    textAlign: opts.textAlign || "left",
    verticalAlign: opts.verticalAlign || "top",
    containerId: opts.containerId || null,
    originalText: opts.originalText || opts.text,
    autoResize: opts.autoResize ?? true,
    lineHeight: brandedTypes.createLineHeight(opts.lineHeight || 1.25),
    version: 1,
  } as ExcalidrawTextElement;
};

/**
 * Update text element with new font while preserving other properties
 * Ensures proper text metrics when font changes
 */
export const updateTextElementFont = (
  element: ExcalidrawTextElement,
  newFontFamily: FontFamilyValues,
): ExcalidrawTextElement => {
  // Validate the new font family
  const fontLoadingManager = FontLoadingManager.getInstance();
  const validatedFontFamily =
    fontLoadingManager.validateFontFamily(newFontFamily);

  // If font didn't change, return original element
  if (validatedFontFamily === element.fontFamily) {
    return element;
  }

  // Get font string for measurements
  const fontString = getFontString({
    fontFamily: validatedFontFamily,
    fontSize: element.fontSize,
  });

  // Clear cached measurements for the old font
  charWidth.clearCache(
    getFontString({
      fontFamily: element.fontFamily,
      fontSize: element.fontSize,
    }),
  );

  // Measure text with new font
  const metrics = measureText(element.text, fontString, element.lineHeight);

  // Return updated element with new font and metrics
  return {
    ...element,
    fontFamily: validatedFontFamily,
    width: metrics.width,
    height: metrics.height,
  };
};
